import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Button, Table, TextInput } from "flowbite-react";
import CampaignRow from "./CampaignRow";
import CreateModal from "./CreateModal";
import { useLists } from "../../contexts/ListsContext";
import { getCampaigns, getInfluencersInCampaigns } from "../../api/campaigns";
import { HiSearch } from "react-icons/hi";
import { debounce } from "lodash";
import Influencers from "./Influencers";

const Campaigns = () => {
  const [openModal, setOpenModal] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const { lists } = useLists();
  const columns = ['Name', 'Brand', 'Status', 'Date created', 'Actions'];
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [lookalikes, setLookalikes] = useState([]);
  const [totalPages, setTotalPages] = useState(1);

  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const _campaigns = await getCampaigns();
      setCampaigns(_campaigns);
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || error.message || 'Failed to fetch campaigns');
    } finally {
      setLoading(false);
    }
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchInfluencersInCampaigns = useCallback(
    debounce(async (search) => {
      try {
        const response = await getInfluencersInCampaigns({
          page,
          s: search,
        });

        setLookalikes(response.influencers || []);
        setTotalPages(response.totalPages || 1);
      } catch (error) {
        console.error(error);
        toast.error(error?.response?.data?.message || error.message || 'Failed to search influencers in campaigns');
      } finally {
        setLoading(false);
      }
    }, 500),
    [page]
  );

  useEffect(() => {
    if (search) {
      setLoading(true);
      fetchInfluencersInCampaigns(search);
    } else {
      fetchCampaigns();
    }
  }, [fetchCampaigns, fetchInfluencersInCampaigns, search]);

  const handleSearch = e => {
    setSearch(e.target.value);
    setPage(1);
  }

  return (
    <>
      <div className="flex gap-4 justify-between items-center border-b border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:flex">
        <h1 className="text-xl font-semibold text-gray-900 dark:text-white sm:text-2xl">Campaigns</h1>
        <div className="flex gap-4">
          <TextInput
            icon={HiSearch}
            placeholder="Search"
            name="s"
            id="s"
            value={search}
            onChange={handleSearch}
          />
          <Button onClick={() => setOpenModal(true)}>
            Create campaign
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="mt-8 text-center text-gray-600">Loading...</div>
      ) : campaigns.length ? (
        <>
          {search ? (
            <Influencers
              lookalikes={lookalikes}
              page={page}
              totalPages={totalPages}
              onPageChange={(value) => setPage(value)}
            />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <Table.Head>
                  {columns.map((column, key) => (
                    <Table.HeadCell key={key}>{column}</Table.HeadCell>
                  ))}
                </Table.Head>
                <Table.Body className="divide-y">
                  {campaigns.map((campaign) => (
                    <CampaignRow
                      campaign={campaign}
                      key={campaign._id}
                      fetchCampaigns={fetchCampaigns}
                    />
                  ))}
                </Table.Body>
              </Table>
            </div>
          )}
        </>
      ) : (
        <div className="mt-8 text-center text-gray-600">No campaigns found</div>
      )}

      <CreateModal
        openModal={openModal}
        setOpenModal={setOpenModal}
        lists={lists}
        fetchCampaigns={fetchCampaigns}
      />
    </>
  );
}

export default Campaigns;